import { Typography, Box, CircularProgress, LinearProgress } from "@mui/material";
import { Lock } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useResetPasswordMutation, useGetUserQuery } from "features/auth/auth";
import { useAppSelector } from "features/store";
import CustomButton from "./common/CustomButton";
import CustomPasswordField from "./common/CustomPasswordField";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";

const passwordRequirements = [
  { label: "At least 8 characters long", test: (p: string) => p.length >= 8 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { label: "One symbol", test: (p: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(p) },
];

interface FieldErrors {
  old_password?: string;
  new_password?: string[];
  confirmNewPassword?: string[];
}

type ResetPasswordInputs = {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

export default function ResetPassword() {
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const webAccessDenied = useAppSelector((state) => state.auth.webAccessDenied);
  const { refetch: refetchUser } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordInputs>();

  const newPassword = watch("newPassword", "");

  const passwordStrength = useMemo(() => {
    const passed = passwordRequirements.filter((req) => req.test(newPassword)).length;
    return (passed / passwordRequirements.length) * 100;
  }, [newPassword]);

  const getStrengthLabel = () => {
    if (passwordStrength <= 25) return { label: "Weak", color: "#f44336" };
    if (passwordStrength <= 50) return { label: "Fair", color: "#ff9800" };
    if (passwordStrength <= 75) return { label: "Good", color: "#2196f3" };
    return { label: "Strong", color: "#4caf50" };
  };

  const strengthInfo = getStrengthLabel();

  const onSubmit: SubmitHandler<ResetPasswordInputs> = async (data) => {
    setServerError(null);
    setFieldErrors({});

    try {
      await resetPassword({
        old_password: data.oldPassword,
        new_password: data.newPassword,
      }).unwrap();

      await refetchUser();
      navigate("/set-email");
    } catch (error: any) {
      if (error?.data?.status === "error" && error?.data?.error) {
        const errorData = error.data.error;
        const newFieldErrors: FieldErrors = {};

        if (errorData.old_password) {
          newFieldErrors.old_password = errorData.old_password;
        }

        if (errorData.new_password) {
          newFieldErrors.new_password = errorData.new_password;
        }

        if (errorData.confirmNewPassword) {
          newFieldErrors.confirmNewPassword = errorData.confirmNewPassword;
        }

        setFieldErrors(newFieldErrors);
      } else {
        const errorMessage = error?.data?.message || "Something went wrong";
        setServerError(errorMessage);
      }

      console.error("Password reset failed:", error);
    }
  };

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      className="rounded-lg bg-white p-6 text-gray-800 "
    >
      <Typography
        component="h1"
        variant="h4"
        className="mb-4  text-[rgb(31,93,107)]"
      >
        Reset password{" "}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        marginTop={1}
      >
        <CustomPasswordField
          id="oldPassword"
          label="Old password"
          autoComplete="current-password"
          error={Boolean(fieldErrors.old_password)}
          helperText={fieldErrors.old_password || ""}
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("oldPassword", {
            required: "Old password is required",
          })}
        />

        <CustomPasswordField
          label="New password"
          id="newPassword"
          autoComplete="new-password"
          error={Boolean(fieldErrors.new_password) || Boolean(errors.newPassword)}
          helperText={
            fieldErrors.new_password?.map((error) => error).join("\n") ||
            errors.newPassword?.message ||
            ""
          }
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("newPassword", {
            required: "New password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
            validate: {
              hasUppercase: (v) => /[A-Z]/.test(v) || "Must contain uppercase letter",
              hasNumber: (v) => /[0-9]/.test(v) || "Must contain a number",
              hasSymbol: (v) =>
                /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(v) || "Must contain a symbol",
            },
          })}
        />

        {newPassword && (
          <Box sx={{ mt: 1, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={passwordStrength}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: strengthInfo.color,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: strengthInfo.color, fontWeight: "bold", mt: 0.5 }}
            >
              {strengthInfo.label}
            </Typography>

            <Box sx={{ mt: 1 }}>
              {passwordRequirements.map((req, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  display="flex"
                  alignItems="center"
                  sx={{
                    color: req.test(newPassword) ? "#4caf50" : "text.secondary",
                  }}
                >
                  {req.test(newPassword) ? "\u2713" : "\u25CB"} {req.label}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <CustomPasswordField
          id="confirmNewPassword"
          label="Confirm new password"
          autoComplete="new-password"
          error={Boolean(fieldErrors.confirmNewPassword) || Boolean(errors.confirmNewPassword)}
          helperText={
            fieldErrors.confirmNewPassword?.join(", ") ||
            errors.confirmNewPassword?.message ||
            ""
          }
          InputProps={{
            startAdornment: <Lock sx={{ color: "gray", mr: 1 }} />,
          }}
          {...register("confirmNewPassword", {
            required: "Please confirm your new password",
            validate: (value) => {
              if (value !== newPassword) return "Passwords do not match";
              return true;
            },
          })}
        />
        {isLoading ? (
          <div className="mt-4 flex justify-center">
            <CircularProgress />
          </div>
        ) : (
          <CustomButton type="submit">Confirm</CustomButton>
        )}

        {serverError && Object.keys(fieldErrors).length === 0 && (
          <Typography
            color="error"
            style={{ marginTop: "16px", textAlign: "center" }}
          >
            {serverError}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
